/**
 * ScrollEngine — virtual scroll with cell pooling and batched binding.
 *
 * Key behavioral subtleties:
 *
 * 1. BIND PATHS: onBind fires with `offsetChangedOnly`. When true, the
 *    delegate should only update position/width (resize path). When false,
 *    it's a full rebind (content + layout). Routing cells through the wrong
 *    path causes either content flash (full when resize suffices) or stale
 *    content (resize when full is needed).
 *
 * 2. `updated` MAP: only contains cells that are NEW at an index (pulled
 *    from pool). These need full rebind because they have no content yet.
 *    Cells that already exist at their index are NOT in `updated` — they
 *    receive offset-only rebinds via emit.all(). This distinction prevents
 *    layout changes (bindLayout) from triggering full content rebuilds on
 *    every visible cell.
 *
 * 3. RECYCLE IDENTITY: toRecycle is a Set<T> tracking by element identity,
 *    not by removed index. When an element is removed from index A and the
 *    pool returns it for index B, toRecycle.delete(element) correctly
 *    prevents onRecycle from firing on an already-reassigned element.
 *
 * 4. BATCH SNAPSHOT: queueOnBind drains the BindBatcher (snapshot + clear)
 *    before iterating. Any queueOnBind calls triggered during onBind (e.g.,
 *    from height invalidation) go into a fresh batch with its own $$.measure,
 *    preventing cascading binds within a single rAF frame.
 *
 * 5. FASTDOM DRAINS: $$.measure callbacks pushed during a flush are
 *    processed in the SAME frame (runTasks uses while/shift). Without the
 *    batch snapshot (#4), invalidateHeight → applyTransaction → queueOnBind
 *    within an onBind callback creates a cascade: bind(A) → bind(B) →
 *    bind(A), converging in ~3 cycles.
 *
 * 6. CONTEXT vs LAYOUT DIRTIED: contextDirtied (data changed) needs full
 *    rebind of all cells. layoutDirtied (width/viewport changed) only needs
 *    resize. bindIndex emits all(false) for the two `IndexBind` kinds that
 *    dirty content (`reanchor`, `refresh`); bindLayout emits all(true) for
 *    layoutDirtied.
 */
import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";
import { NumberRange, NumOptRange, NumRangeLike } from "@tento-core/range";
import { $$ } from "@tento-ui/fastdom";
import { FrameMarks } from "@tento-ui/frame-marks";
import { FRAME_BUDGET, FrameBudget } from "@tento-ui/frame-budget";
import { BindBatcher } from "./bind-batcher";
import { ScrollRangeManager } from "./scroll-range-manager";
import { ScrollViewport } from "./scroll-viewport";
import { ScrollWindow } from "./scroll-window";
import { Scrollable } from "./scrollable";

/**
 * The engine's render epoch, as a type rather than a number with sentinels.
 *
 * One counter drives interruptible rendering: the drain drops any bind stamped
 * with an epoch older than the live one, so the deferred tail of a sheet the
 * user has navigated away from is never painted. It was a bare `number`, and
 * three different meanings were folded into its values — `0` for "inert,
 * feature off", `-1` for "never bound", and a silently-ignored lower value for
 * "regression" — across seven files that each re-spelled the convention. A
 * brand does not stop arithmetic (it widens to `number`, so every breadcrumb
 * payload is unchanged), but it does stop a raw count, a reveal generation, or
 * a `-1` from arriving where an epoch is expected.
 *
 * {@link BindGeneration.ZERO} is the inert epoch and a real value, not an
 * absence: at 0 the drain's gate is `0 < 0`, so nothing is ever dropped, which
 * is exactly the behaviour a consumer that never advances the epoch wants.
 * "Never bound" is a different question and gets its own type
 * ({@link LastBound}) rather than another number.
 */
export type BindGeneration = number & { readonly __brand: "BindGeneration" };

export namespace BindGeneration {
  /** The inert epoch: nothing is ever dropped against it. @see {@link BindGeneration} */
  export const ZERO = 0 as BindGeneration;

  /**
   * Brand a number, or throw.
   *
   * The throw is deliberate and is the one place this rung throws: `-1` and
   * `1.5` are exactly the values the sentinels used to carry, and a boundary
   * that accepted them would leave the type decorative. It follows
   * `AuditRate.parse` — parse at the edge, and past the edge the type is the
   * argument. Contrast {@link ScrollEngine.setBindGeneration}, where a
   * regression is data about a caller rather than a broken value, and is
   * reported instead of thrown.
   */
  export function of(n: number): BindGeneration {
    if (!Number.isInteger(n) || n < 0) {
      throw new Error(`BindGeneration.of: expected a non-negative integer, got ${n}`);
    }
    return n as BindGeneration;
  }

  /** {@link of} over an optional — absence stays absence rather than becoming 0. */
  export function ofOption(n: Option<number>): Option<BindGeneration> {
    return n == null ? null : of(n);
  }

  /** The next epoch. The only way a driver advances one. */
  export function next(g: BindGeneration): BindGeneration {
    return (g + 1) as BindGeneration;
  }

  /**
   * The drain's decision for one queued instruction: does this bind still
   * belong to the render the user is looking at?
   *
   * Extracted because it is the one comparison interruptible rendering rests
   * on, and because "drop-stale" is not "skip" — the caller re-checks every
   * dropped index and re-emits it at the live epoch if the slot survived (the
   * blank-header bug). Naming the answer keeps that obligation attached to it.
   */
  export function gate(
    queued: BindGeneration,
    live: BindGeneration,
  ): "run" | "drop-stale" {
    return queued < live ? "drop-stale" : "run";
  }
}

/**
 * How far the engine has actually BOUND — the answer to "have the binds I am
 * waiting on run?", which an empty queue cannot give.
 *
 * The queue is equally empty in two states: after a waiter's binds drained, and
 * BEFORE they were enqueued. A superseded sheet switch produces the second one,
 * and a waiter that released there acted on work that never happened — the
 * 2026-08-18 hole where a sheet revealed with every scrubbed cell still 0px.
 *
 * `never` is a KIND, not `-1`. The sentinel worked by accident: it compared
 * lower than every real epoch, so it behaved like −∞ in both release rules. It
 * also type-checked everywhere an epoch did, which is how a `-1` reached
 * `whenSettled` as a generation in the first place.
 */
export type LastBound =
  | { readonly kind: "never" }
  | { readonly kind: "bound"; readonly generation: BindGeneration };

export namespace LastBound {
  /** Nothing has bound yet. Shared — an absence is not worth allocating for. */
  export const NEVER: LastBound = { kind: "never" };

  /**
   * What a completed drain leaves behind: `live` if it successfully completed a
   * bind, otherwise whatever was there before.
   *
   * `executed` is successful onBinds — stale drops and thrown binds do not count.
   * That exclusion IS the rule: dropping superseded instructions raises the
   * live epoch without binding anything, so counting a drop-only drain would
   * reopen the empty-queue hole one epoch later. It is also the law the
   * generation fuzzer checks as L2.
   *
   * Monotonic by construction: a drain never moves this backwards, whatever it
   * is handed. The engine cannot produce a lower `live` (the epoch is
   * monotonic), so the guard costs nothing and makes L1 true here rather than
   * true-by-inspection-of-two-files.
   */
  export function afterDrain(
    prev: LastBound,
    executed: number,
    live: BindGeneration,
  ): LastBound {
    if (executed <= 0) return prev;
    if (prev.kind === "bound" && prev.generation >= live) return prev;
    return { kind: "bound", generation: live };
  }

  /** Whether a bind has landed at `generation` or later. `never` is below every
   *  epoch, which is the whole of what the `-1` sentinel used to encode. */
  export function reached(last: LastBound, generation: BindGeneration): boolean {
    return last.kind === "bound" && last.generation >= generation;
  }
}

/**
 * The bind queue's epoch facts, sampled together.
 *
 * Three getters on the engine (`currentBindGeneration`, `lastBoundGeneration`,
 * `hasPendingBinds`) answer one question between them, and a caller that read
 * them a frame apart could see a queue that went idle between the first read
 * and the third — which is what the ProseMirror resume gate in `Sheet` did.
 * One value, one sample.
 *
 * `kind` is `hasPending` as a state rather than a boolean because that is how
 * every reader uses it: "idle" is the precondition for releasing a wait, and
 * "pending" is the instruction to wait again.
 */
export type BindQueueState =
  | {
    readonly kind: "idle";
    readonly current: BindGeneration;
    readonly last: LastBound;
  }
  | {
    readonly kind: "pending";
    readonly current: BindGeneration;
    readonly last: LastBound;
  };

/**
 * What a settle wait is waiting FOR.
 *
 * `ScrollHorizontal.whenSettled` takes one of these rather than a bare number,
 * because a number cannot say whether the waiter wants a bind AT a generation,
 * a bind PAST it, or does not care at all — and all three used to cross this
 * boundary in the same `number` slot: a `-1` sentinel meaning "no debt", a
 * scrub generation the caller had incremented by hand, and a raw reveal
 * generation.
 *
 * The distinction is not decoration. "The bind queue is empty" is true in two
 * states: after the waiter's binds have drained, and BEFORE they have been
 * enqueued. A superseded sheet switch produces the second one — the generation
 * bump makes the drain drop the outgoing instructions, emptying the queue for a
 * microtask or two — and a waiter that releases there acts on work that has not
 * happened. That is how the navigation reveal came to fire with every scrubbed
 * cell still 0px (2026-08-18). Pairing emptiness with a debt makes the two
 * states distinguishable.
 */
export type SettleDebt =
  /**
   * The wait predates any debt: an empty queue is a sufficient release, which
   * is what every caller had before generations existed. Never owed, so it can
   * never block. (Replaces the `NO_BIND_DEBT = -1` sentinel.)
   */
  | { readonly kind: "none" }
  /**
   * A scrub debt: the waiter invalidated something at `generation` and waits
   * for the rebind that repays it. `lastBoundGeneration` is ALREADY at
   * `generation` when such a debt is incurred — the OUTGOING pass bound there —
   * so only a bind STRICTLY past it is evidence the repayment ran (measured,
   * run _c30c04). Release ⟺ `lastBound > generation`.
   */
  | { readonly kind: "incurred-at"; readonly generation: BindGeneration }
  /**
   * A reveal waiting for THIS bind pass: `generation` is the pass's own
   * generation, which nothing has bound at yet, so arriving at it is the
   * evidence. Release ⟺ `lastBound >= generation`.
   */
  | { readonly kind: "this-bind"; readonly generation: BindGeneration };

export namespace SettleDebt {
  /**
   * The ONE definition of "this debt is still outstanding". Every release path
   * in `whenSettled` — the synchronous fast path and the re-arming bounded
   * fallback — asks this question here, so the two can never drift into
   * disagreeing about what the waiter is owed. `SettleFallback.decide` consumes
   * the answer as its `owed` term.
   */
  export function owed(debt: SettleDebt, last: LastBound): boolean {
    switch (debt.kind) {
      case "none":
        return false;
      case "incurred-at":
        // STRICTLY past, not at: the debt's own generation was already bound by
        // the outgoing pass. This is where the callers' hand-written `+1` went.
        // `never` is below every epoch, so nothing bound ⇒ still owed.
        return !LastBound.reached(last, BindGeneration.next(debt.generation));
      case "this-bind":
        return !LastBound.reached(last, debt.generation);
      default: {
        const unhandled: never = debt;
        return unhandled;
      }
    }
  }

  /**
   * {@link owed} against a whole queue sample, plus the queue's own state — the
   * two terms every release path needs, taken from ONE snapshot.
   *
   * Both `whenSettled`'s fast path and its re-arming fallback have to ask "is
   * the queue idle AND is the debt repaid", and each used to read the two off
   * separate engine getters. Between those two reads a drain can complete, so
   * a wait could observe a queue that was pending when it asked about pending
   * and settled when it asked about the debt. The tuple is what
   * `SettleFallback.decide` consumes, unchanged.
   */
  export function against(
    debt: SettleDebt,
    queue: BindQueueState,
  ): { readonly owed: boolean; readonly pending: boolean } {
    return { owed: owed(debt, queue.last), pending: queue.kind === "pending" };
  }
}

/**
 * WHICH path released a settle wait — the evidence the release rests on.
 *
 * `whenSettled` already wrote this leaf into its `scroll/when-settled`
 * breadcrumb; naming it as a type is what lets it also be the wait's RESULT, so
 * a caller can act on the difference instead of a debugger reading it after the
 * fact. The four are not interchangeable: `fast` and `drain` are truthful — the
 * debt was repaid — `fallback` released on a bounded wait with the debt already
 * repaid but no drain to prove it, and `fallback-exhausted` released with the
 * debt STILL OUTSTANDING, liveness beating correctness. A reveal that re-reads
 * its strip (`sheet.ts`) carries this into `sheet/reveal-restrip`, because a
 * strip that moved under an exhausted fallback is a different story from one
 * that moved under a clean drain.
 */
export type SettleRelease =
  | "fast"
  | "drain"
  | "fallback"
  | "fallback-exhausted";

/** Exhaustion and cancellation end a wait without certifying its bind debt. */
export type SettleResult =
  | { readonly kind: "settled"; readonly via: "fast" | "drain" | "fallback" }
  | { readonly kind: "exhausted"; readonly via: "fallback-exhausted" }
  | { readonly kind: "cancelled" };

export class ScrollEngine<T> {
  // @props
  private _viewport: Option<ScrollViewport>;
  get viewport(): Readonly<ScrollViewport> {
    return this._viewport!;
  }
  // The viewport is only created once bindLayout() runs. Callers that can fire
  // before the engine is bound (e.g. scroll-settle/snap during startup) MUST
  // gate on this before touching `viewport`.
  get hasViewport(): boolean {
    return this._viewport != null;
  }
  readonly cellWidth = ((self) => ({
    _default: null as Option<number>,
    get default(): Option<number> {
      return this._default;
    },
    get(index: number): number {
      return (
        self.delegate!.options?.cellWidthForIndex?.(index) ?? this.default!
      );
    },
    getOpt(index: number): Option<number> {
      return self.delegate!.options?.cellWidthForIndex?.(index);
    },
  }))(this);

  /**
   * The index space the live window was built in — what an engine index MEANS
   * right now. Written by every {@link bindLayout}, and the engine's own answer
   * to "did this rebind change the meaning of an index?": the caller used to
   * mirror engine state to answer that (R19's `ColumnSpace` WINDOW mapper) and
   * hand back a translation, which only a caller that already knew the previous
   * state could express.
   *
   * Held ERASED (see {@link ScrollEngine.IndexSpace.Erased}) because one engine
   * outlives any one key type and never inspects a key — it only carries one
   * back to a space to be resolved.
   */
  #space: Option<ScrollEngine.IndexSpace.Erased> = null;

  /** @see {@link #space} — read by consumers whose own fast path must not skip
   *  a rebind that changes the space (`ScrollHorizontal.bindLayout`). */
  get space(): Option<ScrollEngine.IndexSpace.Erased> {
    return this.#space;
  }

  // @state
  readonly window: ScrollWindow = new ScrollWindow({
    onOffsetChanged: (index, offset) => {
      this.fixed.updateOffset(index, offset);
    },
    widthForIndex: (index) => this.cellWidth.get(index),
    // Straight through to the space, which is where the answer lives: how far
    // the axis goes is a fact about what an index MEANS, and #space is the
    // engine's record of that. Before the first bindLayout there is no space
    // and therefore no band to state — not "unlimited by policy", just nothing
    // known yet, and the window's own `±Infinity` reading says exactly that.
    reachableIndices: () =>
      this.#space?.reach() ?? ScrollWindow.Reach.UNBOUNDED,
  });
  readonly fixed = new ScrollRangeManager(this.window);
  private readonly pool: Option<ScrollEngine.Pool<T>>;
  private readonly state: ScrollEngine.State = new ScrollEngine.State(
    this.window,
  );

  readonly entries = ((_self) => ({
    _all: new Map() as Map<number, T>,
    get all(): { t: T; index: number }[] {
      return [...this._all.entries()].map(([index, t]) => ({
        t,
        index,
      }));
    },
    get(index: number): Option<{ t: T; index: number }> {
      const t = this._all.get(index);
      if (!t) return null;
      return { t, index };
    },
  }))(this);

  constructor(
    readonly $root: Scrollable,
    readonly delegate: ScrollEngine.Delegate<T>,
  ) {
    this.pool = new ScrollEngine.Pool(
      this.delegate!.onCreate,
      this.delegate.options?.columnRetention ?? null,
    );
    this.$root.delegate = {
      onUserScrolled: () => {
        this.viewport.visible._indexStartOptimistic = null;
      },
      onCrossAxisScroll: () => {
        this.lastCrossAxisActivityAt = performance.now();
      },
      onScrollActivity: (userInitiated) => {
        if (userInitiated) {
          this.lastUserActivityAt = performance.now();
        }
      },
      onContentOffsetChanged: () => {
        if (this.detached) {
          this.detachedOffsetDirty = true;
          return;
        }
        const previousStart = this.state.visibleIdx.start;
        const transaction = this.state.reconcile(this._viewport!);
        // Index movement survives physical scroll-window recentering. A
        // geometry adjustment must not reverse the content-fill priority.
        this.scrollDirection = Math.sign(this.state.visibleIdx.start - previousStart) || this.scrollDirection;
        this.applyTransaction(transaction, "content-offset-changed");
      },
      // The scrollable owns the DOM's scroll position and the window owns the
      // axis; this is the one wire between them. Read fresh per query rather
      // than pushed on change, because the number becomes knowable as a SIDE
      // EFFECT of the walk the scroll itself triggers — there is no moment to
      // push it from.
      minReach: () => this.window.minReach,
      maxReach: () => this.window.maxReach,
    };
  }

  /*
   Bind
   =============================================================================
   */

  /**
   * Rebind the geometry inputs — the uniform cell width and the viewport's
   * length/occlusion. Two orderings in here are load-bearing.
   *
   * 1. THE NEW WIDTH IS INSTALLED BEFORE THE WINDOW IS RESET. `markLayoutDirtied`
   *    wipes the index↔pixel map, and every width query that follows the reset
   *    (`ScrollWindow.extendTo*` → `widthForIndex` → `cellWidth.get`) has to read
   *    the NEW stride. Assigning `cellWidth._default` after `markLayoutDirtied`
   *    (the previous order) let the first re-derivation of the window — including
   *    any re-entrant `extendToIdxRange` from the fixed-child updates that
   *    `reset` → `maybeEmitZeroIndex` fans out — run at the PREVIOUS config's
   *    column width, so the rendered range was sized in old-width columns.
   *
   * 2. THE WINDOW IS RE-ANCHORED BEFORE THE RECONCILE. The reset re-origins the
   *    map at index 0 ⇒ 0px and zeroes `offsetBase`, but the scrollable still
   *    sits at a pixel position measured in the PREVIOUS layout. Reconciling
   *    straight away therefore reads `rendered.offsetRange` out of one coordinate
   *    space and resolves it in another: `findIndiciesFromOffsetRange` runs off
   *    the end of the one-cell window and the rendered range collapses to a
   *    handful of columns clustered at the origin — the header/body strip the
   *    user is looking at gets no columns at all, and nothing re-reconciles until
   *    a later user scroll happens to land the two spaces back in agreement.
   *    So re-derive the window at the NEW stride around the column the user was
   *    on (padding = live viewport ÷ new cell width, the same arithmetic
   *    `bindIndex` uses) and put the scrollable back on it. That reproduces —
   *    immediately, and without moving the user — the self-consistent state a
   *    scroll produces today.
   *
   * 3. THE ANCHOR CROSSES INDEX SPACES BY THE ENGINE'S OWN RECKONING. Index
   *    meaning is caller-defined: when a layout flip also changes what an index
   *    MEANS — an hour-columnar sheet giving way to a day-columnar one, or two
   *    sheets whose index 0 anchors to different dates — preserving the raw
   *    index re-anchors the window arbitrarily far from the content the user was
   *    on (observed live 2026-08-06: hour-index −109 re-anchored a day view 109
   *    days into the past). So every rebind NAMES the {@link
   *    ScrollEngine.IndexSpace} its layout belongs to and the engine does the
   *    rest: it remembers the space its window was built in, compares `key`s
   *    itself, and carries the anchor's CONTENT across when they differ
   *    (`incoming.indexOf(previous.keyAt(anchor))`). What it takes is STATE —
   *    "this layout is in space S" — where it used to take a transition, "here
   *    is how to get from whatever you had to what I am handing you". Only a
   *    caller holding a mirror of engine state could phrase the second one, and
   *    keeping that mirror correct was the caller's whole job (R19's
   *    `ColumnSpace`); R21 gave the question back to the side that owns the
   *    answer.
   *
   * There is no return value, and no nullable space: a consumer that has not
   * thought about index spaces cannot express that by omission —
   * {@link ScrollEngine.IndexSpace.identity} is the positive claim that indices
   * ARE the content (a vertical list of rows).
   *
   * A rebind that changes the SPACE at unchanged geometry, and whose anchor does
   * not move, still has work to do: per-index widths come from the new space, so
   * the offsets the window holds are the OLD space's. That is the day-view
   * "Personal Timezones" toggle, and the one case the geometry-equality guard
   * would otherwise drop on the floor — it reflows instead of returning.
   */
  bindLayout<K extends ScrollEngine.ContentKey>(
    layout: ScrollEngine.LayoutProps,
    space: ScrollEngine.IndexSpace<K>,
  ): void {
    Tracing.info("ui")("core/scroll-engine/bind-layout", layout);

    const prevViewport = this._viewport;
    const prevProps = prevViewport?.props;
    const prevWidth = this.cellWidth._default;
    const prevSpace = this.#space;
    // The incoming space, viewed as the engine holds every space: at the key
    // type it does not know. `keyAt` on the outgoing space and `indexOf` on this
    // one meet in that erased type, which is the whole of the pairing — nothing
    // below reads a key, it only carries one across.
    const incoming: ScrollEngine.IndexSpace.Erased = space;

    const eqCellWidth = prevWidth === layout.cellWidthPx;
    const eqVp = ScrollViewport.Props.equals(layout, prevProps);

    // Read the anchor through the OUTGOING viewport + the not-yet-reset window:
    // it is the only place the column currently under the viewport's left edge
    // is still expressible. `findIndexForOffsetMin` yields null off the ends of
    // the window, so this can be non-finite — guarded at the use site.
    const anchorIndex = prevViewport
      ? prevViewport.visible.anchorIndex
      : null;
    const canAnchor = anchorIndex != null && Number.isFinite(anchorIndex);

    // A different space is the whole reason to look at the anchor at all, and
    // `key` is the only thing compared — reference identity, so a space is the
    // same space exactly when it is the same space. Geometry equality does not
    // imply index-space equality: two configs can resolve to the same column
    // width (week x 6 and month x 6 both divide the same strip into six) while
    // index N stops meaning a week and starts meaning a month. Judging "nothing
    // changed" on pixels alone let that flip through untouched, preserving the
    // raw index across the boundary — week 38 became month 38, three years out.
    const spaceChanged = prevSpace != null && prevSpace.key !== space.key;

    // Carry the anchor by its CONTENT: the column under the left edge is asked
    // for its key in the space it was rendered in, and the incoming space is
    // asked where that content now lives.
    const translated = spaceChanged && canAnchor &&
        prevSpace != null && anchorIndex != null
      ? incoming.indexOf(prevSpace.keyAt(anchorIndex))
      : anchorIndex;

    // A translated index is caller-supplied data, and the window charges for it
    // by the index: `ScrollWindow` stores offsets in a dense array subscripted by
    // engine index and walks one index at a time, so every unit between the
    // origin and the anchor costs an array slot and a delegate callback. Taking
    // that number on trust is how a mapper bug becomes a frozen tab — a
    // translation returning 1e12 spent 6.8 seconds walking before the array
    // length threw (measured 2026-08-18). So the anchor is validated before the
    // window is asked to reach it, and a rejected one falls back to the
    // UNTRANSLATED anchor: that index came out of the live window, so it is
    // representable by construction and the strip stays covered. This is the one
    // place index-preserving re-anchor is still the right answer, and it is not
    // silent — the whole point of the fallback is the report next to it.
    const rejectedAnchor = canAnchor &&
      translated !== anchorIndex &&
      !ScrollEngine.isAddressableAnchor(translated);
    if (rejectedAnchor) {
      Tracing.err("ui")("core/scroll-engine/anchor-translate-rejected", {
        from: anchorIndex,
        to: translated,
        limit: ScrollEngine.MAX_REANCHOR_SPAN,
      });
      Tracing.log("scroll/anchor-translate-rejected", {
        from: anchorIndex,
        to: translated,
        limit: ScrollEngine.MAX_REANCHOR_SPAN,
      });
    }
    const anchoredIndex = rejectedAnchor ? anchorIndex : translated;

    // The ACCEPTED anchor, not the proposed one: a rejected translation falls
    // back to the index the window already held, so it has not moved and there
    // is nothing to rebuild for.
    const anchorMoved = canAnchor &&
      anchoredIndex != null &&
      Number.isFinite(anchoredIndex) &&
      anchoredIndex !== anchorIndex;

    const changed = !eqCellWidth || !eqVp || anchorMoved;

    this.cellWidth._default = layout.cellWidthPx;
    this._viewport = new ScrollViewport(
      layout,
      this.window,
      () => this.$root!.contentOffset,
    );
    // BEFORE any reconcile or emit, both of which run synchronously below: the
    // consumer resolves every index in a `layout-changed` emit through the space
    // it believes is current, and those indices are minted in the INCOMING one.
    // Installing it afterwards is R19's defect exactly — a rendered end of −2422
    // read through the outgoing year mapper came back as the year −396.
    this.#space = space;
    // Retained spares are keyed by index; a new space or a rebuilt window
    // changes what an index shows, so none of them may come back by key.
    if (spaceChanged || changed) this.pool?.forget();

    if (!changed) {
      // A new space at identical geometry whose anchor did not move: the window
      // is positionally right and dimensionally stale, because `widthForIndex`
      // now answers out of a different space. Reflow the per-index offsets in
      // place — this is R19's `ColumnSpace` BOUND question, asked by the one
      // object that can see both spaces. Never both this and the rebuild below:
      // the rebuild resets the window and re-derives every offset anyway.
      if (spaceChanged) this.reflowWidths();
      return;
    }

    // Past here the window is rebuilt, then reconciled and emitted so cells
    // re-render with the new width. Without that emit, layout changes from the
    // sidebar toggle (which uses a CSS transform and so fires no ResizeObserver)
    // would never apply.
    this.state.markLayoutDirtied();

    const viewport = nonnull(this._viewport);
    if (
      anchoredIndex != null &&
      Number.isFinite(anchoredIndex) &&
      layout.cellWidthPx > 0
    ) {
      const padding = Math.max(
        1,
        Math.ceil(viewport.visible.length / layout.cellWidthPx),
      );
      const range = {
        start: anchoredIndex - padding,
        end: anchoredIndex + 2 * padding,
      };
      const outcome = this.window.extendToIdxRange(range);
      // A refusal leaves the PREVIOUS window in place, which is the right
      // fallback: it is the map the strip on screen was painted against, so
      // keeping it keeps the columns covered while the breadcrumb says the
      // anchor was unreachable. Loud but never thrown — this runs inside the
      // engine's own reconcile, and an exception here takes the sheet down.
      if (outcome.kind === "refused") {
        this.reportRefusedExtend(outcome, range, "layout-changed");
      } else {
        const anchorOffset = viewport.scrollOffset.start(anchoredIndex);
        if (anchorOffset != null) {
          this.$root.scrollTo(anchorOffset, "instant", false, true);
        }
      }
    }

    const tx = this.state.reconcile(viewport);
    this.applyTransaction(tx, "layout-changed");
    this.emit.all(true, "layout-changed");

    // One breadcrumb per real geometry change (the equality guard above makes
    // this bounded): the width transition, the range it produced, and the strip
    // that range has to cover. A `rendered` span narrower than
    // `viewportPx / nextWidth` is the blank-strip defect reappearing.
    Tracing.log("scroll/layout-rebind", {
      prevWidth,
      nextWidth: layout.cellWidthPx,
      rendered: [this.state.renderedIdx.start, this.state.renderedIdx.end],
      viewportPx: viewport.visible.length,
      anchorFrom: anchorIndex,
      anchorTo: anchoredIndex,
    });
  }

  /** Install geometry at an explicit incoming target. Unlike a layout-only
   * change, a sheet switch has no outgoing anchor to preserve first. */
  bindLayoutAt<K extends ScrollEngine.ContentKey>(
    layout: ScrollEngine.LayoutProps,
    space: ScrollEngine.IndexSpace<K>,
    index: number,
    bind: Extract<ScrollEngine.IndexBind, { kind: "reanchor" }>,
  ): void {
    if (!ScrollEngine.isAddressableAnchor(index)) {
      throw new Error(`Unaddressable layout anchor: ${index}`);
    }
    this.cellWidth._default = layout.cellWidthPx;
    this._viewport = new ScrollViewport(layout, this.window, () => this.$root!.contentOffset);
    this.#space = space;
    // bindIndex owns the single reset, real scroll write, reconcile and full
    // content emission. All synchronous callbacks see the incoming space.
    this.bindIndex(index, bind);
  }

  /**
   * Report a walk the window would not take. Two lines on purpose: the
   * `Tracing.err` is for a human reading a live console, and the `witness/`
   * line is for the storm fuzzer's oracle, whose battery-wide forbid on
   * `witness/*` turns any occurrence into a failing program with the numbers
   * attached. Never throws — this is called from inside the engine's own
   * reconcile, where an exception takes the sheet down with it.
   */
  private reportRefusedExtend(
    outcome: ScrollWindow.ExtendOutcome.Refused,
    range: NumRangeLike,
    at: string,
  ): void {
    const payload = {
      span: outcome.span,
      max: outcome.max,
      from: range.start,
      to: range.end,
      at,
    };
    Tracing.err("ui")("core/scroll-engine/window-span-refused", payload);
    Tracing.log("witness/scroll-window-span", payload);
  }

  /**
   * Recycle and discard every cell — live entries and pooled spares alike —
   * handing each to `dispose`, and leave the engine to re-create cells
   * through the factory on the next dirty reconcile. This is the verb for a
   * host whose cell SHAPE changes (a chat thread swapping render registers):
   * the pool assumes a recycled cell comes back as the same kind of element,
   * so a shape change must drain the pool rather than morph its members.
   * The caller owns detaching the disposed elements from the DOM and
   * triggering the rebind that repopulates.
   */
  resetCells(dispose: (t: T) => void): void {
    const pool = nonnull(this.pool);
    for (const [, t] of this.entries._all) {
      this.delegate.onRecycle(t);
      dispose(t);
    }
    this.entries._all.clear();
    for (const t of pool.drain()) dispose(t);
    // The state still claims the rendered range is rendered — and with
    // unchanged geometry the next bindLayout short-circuits and the next
    // reconcile sees no index movement, so nothing would ever re-add the
    // cells just disposed. Mark content dirty (no re-anchor: the view has
    // not moved) so the next reconcile force-adds the whole rendered range
    // through the factory.
    this.state.markContextDirtied(false);
  }

  invalidateHeight(index: number, anchor: boolean = true): void {
    let anchorIndex: Option<number> = null;
    let anchorOffsetBefore: Option<number> = null;

    if (anchor && this._viewport) {
      anchorIndex = this._viewport.visible.indexRange.start;
      if (anchorIndex != null) {
        anchorOffsetBefore = this.window.getOffset(anchorIndex);
      }
    }

    this.window.recalculateOffsetsFrom(index);

    if (
      anchorIndex != null &&
      anchorOffsetBefore != null &&
      index < anchorIndex
    ) {
      const anchorOffsetAfter = this.window.getOffset(anchorIndex);
      if (anchorOffsetAfter != null) {
        const delta = anchorOffsetAfter - anchorOffsetBefore;
        if (delta !== 0) {
          if (delta > 0) {
            // Growth: fully absorb into offsetBase (shifts cells above anchor negative)
            this.window.offsetBase -= delta;
          } else {
            // Shrinkage: absorb with offsetBase first, scrollTop remainder
            const newOffsetBase = this.window.offsetBase - delta;
            if (newOffsetBase <= 0) {
              this.window.offsetBase = newOffsetBase;
            } else {
              this.window.offsetBase = 0;
              this.$root.adjustContentOffset(-newOffsetBase);
            }
          }
          // Fire negative overflow callback based on offsetBase
          const cell0Offset = this.window.getOffset(this.window.minIndex);
          const overflow =
            cell0Offset != null && cell0Offset < 0 ? -cell0Offset : 0;
          this.delegate?.options?.onNegativeOverflow?.(overflow);
        }
      }
    }

    this.emit.all(true, "height-invalidation");
  }

  /**
   * Re-flow per-index offsets after the per-index WIDTH source (the column
   * mapper) changed while the uniform cell width and viewport did NOT — the
   * geometry mutation {@link bindLayout}'s width/viewport-equality guard silently
   * drops. The day-view "Personal Timezones" toggle rebuilds the view model with
   * a new index mapper under the same sheet id, so in-flight columns flip between
   * a fixed 400px and the uniform width; but neither the uniform width nor the
   * viewport moves, so `bindLayout` short-circuits and the ScrollWindow keeps the
   * previous mapper's offsets — columns render at stale positions. Mirrors
   * {@link invalidateHeight} for the horizontal axis: recompute offsets from the
   * window origin with the live `widthForIndex`, absorb the shift at the visible
   * anchor into `offsetBase` so the on-screen column does not jump, then emit so
   * every rendered column rebinds at its new position. A no-op while unbound.
   */
  reflowWidths(): void {
    if (!this._viewport) return;
    const anchorIndex = this._viewport.visible.indexRange.start;
    const anchorOffsetBefore =
      anchorIndex != null ? this.window.getOffset(anchorIndex) : null;

    this.window.recalculateOffsetsFrom(this.window.minIndex);

    if (anchorIndex != null && anchorOffsetBefore != null) {
      const anchorOffsetAfter = this.window.getOffset(anchorIndex);
      if (anchorOffsetAfter != null) {
        const delta = anchorOffsetAfter - anchorOffsetBefore;
        if (delta !== 0) this.window.offsetBase -= delta;
      }
    }

    this.emit.all(true, "layout-changed");
  }

  rebindViewport() {
    if (!this._viewport) return;
    const transaction = this.state.reconcile(this._viewport);
    this.applyTransaction(transaction, "viewport-size-changed");
  }

  /**
   * Re-bind the window around `index`, doing exactly what `bind` claims and
   * nothing else. The three questions every entry point on this engine answers
   * are settled by {@link ScrollEngine.IndexBind}'s kind alone:
   *
   * > **RESET** the index↔pixel map — `reanchor` only.
   * > **MOVE** the view — `reanchor` and `reveal`.
   * > **EMIT** `all` — `reanchor` and `refresh`.
   *
   * Only a genuine re-anchor (a sheet or timescale change) resets the offset
   * window: an in-place refresh leaves cell widths unchanged, so the window is
   * still valid, and resetting it there would zero `offsetBase` and snap the
   * view. Where content IS dirtied (`reanchor`, `refresh`) the reconcile
   * re-adds the whole rendered range and `emit.all` rebinds it.
   *
   * Returns what it did, in those same three terms — see
   * {@link ScrollEngine.IndexBindResult}.
   */
  bindIndex(
    index: number,
    bind: ScrollEngine.IndexBind,
  ): ScrollEngine.IndexBindResult {
    // Detached (a late snap timer, a navigator reveal after the sheet left the
    // document): keep the intent, move nothing, emit nothing. `resume()` runs
    // the last retained bind, which reconciles and emits for itself.
    if (this.detached) {
      this.detachedBindIndex = { index, bind };
      const moved = bind.kind === "reanchor" || bind.kind === "reveal";
      return { reanchored: bind.kind === "reanchor", moved, emitted: false };
    }
    const revision = ++this.indexBindRevision;
    const viewport = nonnull(this._viewport);
    const current = () => this.indexBindRevision === revision && this._viewport === viewport;
    const reanchored = bind.kind === "reanchor";
    const moved = bind.kind === "reanchor" || bind.kind === "reveal";
    const emitted = bind.kind === "reanchor" || bind.kind === "refresh";
    const at: Option<ScrollEngine.RevealAt> = moved ? bind.at : null;
    const viewportChangeSource = bind.viewportChangeSource;

    if (emitted) this.state.markContextDirtied(reanchored);
    // A re-anchor (a sheet or timescale change) moves what each index shows.
    if (reanchored) this.pool?.forget();

    //
    // 1) Extend the window offsets
    //
    const padding = Math.ceil(
      this._viewport!.visible.length / this.cellWidth._default!,
    );
    const range = { start: index - padding, end: index + 2 * padding };
    const outcome = this.window.extendToIdxRange(range);
    // Same contract as `bindLayout`'s extend: an index the window will not walk
    // to leaves the map untouched and says so. The bind continues — the offset
    // lookups below simply resolve against the window that is still there.
    if (outcome.kind === "refused") {
      this.reportRefusedExtend(outcome, range, "bind-index");
    }

    //
    // 2) Scroll the underlying scrollable (the two kinds that do not move
    //    still resolve the offset: the navigator emit below reads it).
    //
    // `refresh`/`reconcile` never carried a position — "start" is what the old
    // options bag resolved to for them, and the value only reaches the
    // navigator emit below.
    const targetScrollOffset = this.viewport.scrollOffset.position(
      index,
      this.cellWidth.get(index),
      at != null ? at.position : "start",
    )!;

    if (at != null) {
      this.$root?.scrollTo(
        targetScrollOffset,
        at.scrollBehavior,
        at.useNativeScrollTo,
        this.state.contains(index),
      );
    }

    //
    // 3) Apply the transaction
    //
    if (!current()) return { reanchored, moved, emitted: false };
    const transaction = this.state.reconcile(viewport);
    if (!current()) return { reanchored, moved, emitted: false };
    this.applyTransaction(transaction, "bind-index", viewportChangeSource);
    if (!current()) return { reanchored, moved, emitted: false };
    if (emitted) this.emit.all(false, "bind-index", viewportChangeSource);
    if (!current()) return { reanchored, moved, emitted };
    if (viewportChangeSource === "navigator") {
      const targetVisibleStart =
        targetScrollOffset + this.viewport.props.startOccludedPx;
      this.emit.visibleRange(
        this.window
          .findIndiciesFromOffsetRange(
            new NumberRange(
              targetVisibleStart,
              targetVisibleStart + this.viewport.visible.length,
            ),
          )
          .clamp(this.viewport.props.minIndex, this.viewport.props.maxIndex),
        viewportChangeSource,
      );
    }
    // Delegate callbacks may synchronously install a successor. Its relative
    // navigation anchor belongs to that operation, even if this call unwinds last.
    if (current()) viewport.visible._indexStartOptimistic = index;
    return { reanchored, moved, emitted };
  }

  private viewportChangeSourceForContext(
    context: string,
  ): ScrollEngine.ViewportChangeSource {
    switch (context) {
      case "content-offset-changed":
        return "scroll";
      case "height-invalidation":
      case "layout-changed":
      case "viewport-size-changed":
        return "layout";
      default:
        return "bind";
    }
  }

  private applyTransaction(
    transaction: ScrollEngine.Transaction,
    context: string,
    viewportChangeSource: ScrollEngine.ViewportChangeSource =
      this.viewportChangeSourceForContext(context),
  ) {
    const pool = nonnull(this.pool);
    const updated: Map<number, T> = new Map();

    if (transaction.renderedChanged) {
      this.emit.willRender();

      const toRecycle = new Set<T>();
      const removeIdx = (idx: number) => {
        const e = this.entries._all.get(idx);
        if (!e) return;
        this.entries._all.delete(idx);
        pool.recycle(e, idx);
        toRecycle.add(e);
      };
      const addIdx = (idx: number) => {
        let e = this.entries._all.get(idx);
        if (!e) {
          e = pool.get(idx, this.state.renderedIdx);
          this.entries._all.set(idx, e);
          updated.set(idx, e);
        }
        toRecycle.delete(e);
      };

      for (const range of transaction.changeset.removes) {
        for (let i = range.start; i <= range.end; ++i) removeIdx(i);
      }
      for (const range of transaction.changeset.adds) {
        for (let i = range.start; i <= range.end; ++i) addIdx(i);
      }
      for (const e of toRecycle) {
        this.delegate!.onRecycle(e);
      }
      FRAME_BUDGET.noteReconciled(updated.size);

      this.emit.rendered();
    }

    if (transaction.visibleChanged) {
      // Idle work waits while any visible range moves, programmatic scrolls included.
      FRAME_BUDGET.noteViewportMoved();
      this.emit.visible(viewportChangeSource);
    }

    this.emit.indices(false, context, updated);
  }

  readonly emit = ((self) => ({
    all(
      widthChangeOnly: boolean,
      context: string,
      viewportChangeSource: ScrollEngine.ViewportChangeSource =
        self.viewportChangeSourceForContext(context),
    ) {
      this.visible(viewportChangeSource);
      this.willRender();
      this.indices(widthChangeOnly, context);
      this.rendered();
    },
    visible(viewportChangeSource: ScrollEngine.ViewportChangeSource) {
      // Detached, only the final range matters: remember the latest source;
      // resume's drain delivers it once.
      if (self.detached) {
        self.heldVisibleEmit = viewportChangeSource;
        return;
      }
      this.visibleRange(self.state!.visibleIdx, viewportChangeSource);
    },
    visibleRange(
      range: NumRangeLike,
      viewportChangeSource: ScrollEngine.ViewportChangeSource,
    ) {
      self.delegate?.options?.onVisibleIndiciesChanged?.(
        range,
        viewportChangeSource,
      );
    },
    willRender() {
      self.delegate?.options?.onRenderedIndiciesWillChange?.(
        self.state!.renderedIdx,
      );
    },
    rendered() {
      self.delegate?.options?.onRenderedIndiciesChanged?.(
        self.state!.renderedIdx,
      );
    },
    indices(
      widthChangeOnly: boolean,
      context: string,
      elements: Map<number, T> = self.entries._all,
    ) {
      if (elements.size <= 0) return;
      Tracing.dev(`scroll/rebind/reason=${context}`);
      $$.startInstrumentation(`scroll/${context}`, 5);
      for (const [idx, t] of elements.entries()) {
        self.queueOnBind(t, idx, widthChangeOnly);
      }
    },
    index(index: number) {
      const t = self.entries.get(index);
      if (!t) return;
      // Rebind index
      Tracing.dev("dispatchOnBind/rebind-index");
      self.queueOnBind(t.t, t.index);
    },
  }))(this);

  private readonly bindBatcher = new BindBatcher<T>();
  private onBindBatchQueue = false;
  // Current render epoch (interruptible rendering). queueOnBind stamps it onto
  // each batched request; the drain drops any instruction tagged with an OLDER
  // generation — the deferred bind tail of a sheet the user has navigated away
  // from. 0 (the default, never advanced) ⇒ inert: `gen < bindGeneration` is
  // `0 < 0` = false, nothing is dropped. The bind-driving layer (Sheet) calls
  // setBindGeneration once per bind when the feature is enabled.
  private bindGeneration: BindGeneration = BindGeneration.ZERO;
  private pendingBindInstallation: Option<BindGeneration> = null;
  private bindInstallationRevision = 0;

  private get awaitingBindInstallation(): boolean {
    return this.pendingBindInstallation === this.bindGeneration;
  }
  /**
   * True while any rendered index is enqueued for (re)bind but not yet drained —
   * it transiently reads as unbound (null `cid`) though it will bind on an
   * upcoming `$$.measure`. A consumer gating a post-bind invariant check
   * (`ScrollHorizontal.assertChildren`) MUST skip while this holds: such an index
   * is pending-bind, not stranded. When the batch drains empty it fires
   * `onBatchComplete`, which re-runs the check cleanly with every `cid` set.
   */
  /** The pool's hits, retargets and creations since the last call, and its spares now (`Pool.takeStats`). */
  takeColumnRetentionStats(): ScrollEngine.Pool.Stats & { spares: number } {
    return nonnull(this.pool).takeStats();
  }

  /**
   * Independent operands of the pending predicate, captured without scheduling,
   * layout reads or queue traversal. Diagnostic data only; never a settle gate.
   */
  diagnosticPendingBinds() {
    return {
      installation: this.awaitingBindInstallation,
      batchNonempty: !this.bindBatcher.isEmpty,
      drainScheduled: this.onBindBatchQueue,
      background: FRAME_BUDGET.hasBackgroundDebt(),
      generation: this.bindGeneration,
      lastBound: { ...this._lastBound },
      lifecycle: this.lifecycle,
      detached: this.detached,
      installationRevision: this.bindInstallationRevision,
      unrecordedGeneration: this.unrecordedBinds.generation,
      unrecordedCount: this.unrecordedBinds.count,
    };
  }

  get hasPendingBinds(): boolean {
    return this.awaitingBindInstallation || !this.bindBatcher.isEmpty || this.onBindBatchQueue ||
      FRAME_BUDGET.hasBackgroundDebt();
  }

  /**
   * Background work queued against the shared frame budget (calendar strip
   * construction) drained: re-run the quiesce check so `onBatchComplete`
   * fires once content, not only column binds, is complete.
   */
  private readonly onBackgroundIdle = (): void => {
    if (!this.detached) this.scheduleDrain();
  };

  /**
   * True while a bind for `index` is queued at the live epoch — one the drain
   * will run, not drop as stale. A budget tail is re-enqueued, never
   * discarded, so this is a bind that is coming: the
   * per-index form of {@link hasPendingBinds}, for a consumer asking whether a
   * column's "awaiting bind" marker still has its clearing bind ahead of it.
   * Queued is not runnable: while {@link bindInstallationPending} holds, the
   * drain waits on `commitBindGeneration`, and a caller that treats the queued
   * bind as progress must also ask whether that barrier is lifting.
   */
  hasLiveBindQueued(index: number): boolean {
    const queued = this.bindBatcher.generationAt(index);
    return queued != null && BindGeneration.gate(queued, this.bindGeneration) === "run";
  }

  /**
   * True while the live generation's bind installation barrier is held
   * (`beginBindGeneration` ran, the matching `commitBindGeneration` has not):
   * no bind drains until it lifts, queued or not. The driver releases it at
   * the end of its incoming pass; a pass that throws leaves it held until a
   * retry or a newer generation supersedes it.
   */
  get bindInstallationPending(): boolean {
    return this.awaitingBindInstallation;
  }

  /** A queued bind will revisit this index, including budgeted work. */
  isBindQueued(index: number): boolean {
    return this.bindBatcher.has(index);
  }
  // Per-frame wall-clock budget for the onBind drain. A hard fling or cold start
  // can queue more binds than fit in one ~16ms frame; draining them all at once
  // blocks paint (observed up to ~40ms). Instead we spend at most this long per
  // frame and re-enqueue the rest for the next $$.measure — `drain` re-resolves
  // occupants against the LIVE rendered range, so indices the user has scrolled
  // past are dropped. The rendered range is the staleness epoch; no timers/debounce.
  private static readonly DRAIN_BUDGET_MS = 8;
  /** Monotonic time of the last cross-axis (vertical) scroll event; with the frame deadline, either axis moving picks the motion deadline. */
  private lastCrossAxisActivityAt = -Infinity;
  private scrollDirection = 1;

  /** Monotonic time of the last USER scroll event (`Scrollable.Delegate.onScrollActivity`). */
  private lastUserActivityAt = -Infinity;
  /**
   * The latest visible-range change that arrived while detached (`dispose`):
   * `resume()`'s drain delivers it once. Geometry emits are unaffected.
   */
  private heldVisibleEmit: Option<ScrollEngine.ViewportChangeSource> = null;

  /**
   * Lifecycle epoch. Every scheduled callback — the frame a drain runs in —
   * captures it when scheduled and returns untouched if it has moved. `dispose()` advances it, which
   * invalidates everything scheduled before a detach without reviving it
   * later: a reconnect schedules fresh work under the new epoch via `resume()`.
   */
  private lifecycle = 0;
  /**
   * Detached: between `dispose()` and `resume()`. Not the same thing as the
   * epoch — the epoch invalidates callbacks scheduled BEFORE a detach; this
   * flag is what work arriving AFTER one (a snap timer, a provider re-emit, a
   * late scroll event) checks. While detached, model state and debt still
   * accumulate (the batcher, the held visible range, a dirty offset) but
   * nothing schedules a frame, delivers a visible range, or moves a column.
   */
  private detached = false;
  /** A scroll offset changed while detached; `resume()` reconciles once. */
  private detachedOffsetDirty = false;
  /** A direct `bindIndex` (a late snap, a navigator reveal) that arrived while
   *  detached: the intent is kept — last one wins — and executed by `resume()`. */
  private detachedBindIndex: Option<{ index: number; bind: ScrollEngine.IndexBind }> = null;

  /**
   * Detach: reversible. Invalidates every queued drain through the epoch and
   * forgets the gesture's input times. Queued bind
   * debt is KEPT — a detached sheet can be re-appended (the sheet's disconnect
   * supports reparenting and pool reuse) and `resume()` then drains it under
   * the new epoch. Nothing here is permanent; the adapter builds a fresh engine
   * only when it re-initializes.
   */
  dispose(): void {
    this.lifecycle += 1;
    this.detached = true;
    FRAME_BUDGET.noteFrameWork(this, false);
    this.detachedBindIndex = null;
    // A drain scheduled before the detach will see the old epoch and skip;
    // clear the flag so `resume()` can schedule a fresh one.
    this.onBindBatchQueue = false;
    this.lastUserActivityAt = -Infinity;
    this.lastCrossAxisActivityAt = -Infinity;
  }

  /**
   * Reattach after `dispose()`: any bind debt or visible-range debt that was
   * queued at detach time gets a fresh drain under the current epoch. Idempotent
   * and harmless on an engine with nothing owed.
   */
  resume(): void {
    if (!this.detached) return;
    this.detached = false;
    const retained = this.detachedBindIndex;
    this.detachedBindIndex = null;
    if (retained != null && this._viewport != null) {
      // A retained bindIndex reconciles for itself; a dirty offset is subsumed.
      this.detachedOffsetDirty = false;
      this.bindIndex(retained.index, retained.bind);
    } else if (this.detachedOffsetDirty && this._viewport != null) {
      this.detachedOffsetDirty = false;
      const transaction = this.state.reconcile(this._viewport);
      this.applyTransaction(transaction, "resume");
    }
    if (!this.bindBatcher.isEmpty || this.heldVisibleEmit != null) this.scheduleDrain();
  }

  /**
   * Monotonic time of the last user scroll event, or `null` if none since the
   * last detach. A settle waiter samples it every frame and measures its
   * pending budget from the gesture's end.
   */
  lastUserInputAt(): Option<number> {
    return Number.isFinite(this.lastUserActivityAt) ? this.lastUserActivityAt : null;
  }

  /** Visible-range debt kept while detached, for resume (tests and diagnostics). */
  get hasHeldVisibleEmit(): boolean {
    return this.heldVisibleEmit != null;
  }

  /**
   * Advance the render epoch. Binds queued under an older generation are dropped
   * in the drain loop (see {@link bindGeneration}). Monotonic — a lower value is
   * ignored. Called once at the top of a sheet bind when interruptible rendering
   * is enabled; otherwise the generation stays 0 and the drain never drops.
   */
  setBindGeneration(generation: BindGeneration) {
    if (generation < this.bindGeneration) {
      // Reported, not thrown, and the lower value is still ignored. Three
      // reasons, and the first is the binding one: the call is synchronous
      // inside `Sheet.bind`'s switch path, after the scrub and before the
      // incoming pass enqueues, so throwing here leaves the outgoing sheet
      // half-scrubbed with the spinner up — the reveal-path rule `cell-height`
      // states verbatim (loud, never block the reveal). Ignoring is already
      // the correct recovery: the higher epoch is the truth, and adopting the
      // lower one would resurrect binds the drain already dropped. And
      // `witness/` is forbidden battery-wide, so this breadcrumb is a failing
      // run rather than a diagnostic nobody reads.
      //
      // Unreachable today — `Sheet` is the engine's only driver and increments
      // before every set. It is a tripwire for the second driver.
      Tracing.log("witness/bind-generation-regression", {
        from: this.bindGeneration,
        to: generation,
      });
      return;
    }
    if (generation > this.bindGeneration) {
      const wasAwaitingInstallation = this.awaitingBindInstallation;
      this.bindGeneration = generation;
      // A new render epoch is a new coverage-failure episode: the categorical
      // attempt cap and the once-per-state stuck dedup restart with it. The
      // repair latch goes with them harmlessly — its keys embed the
      // generation, so no post-advance key could match a pre-advance one.
      this.coverageEpisode = null;
      // A plain epoch advance keeps its legacy dispatch semantics. An older
      // installation cannot hold a caller that did not begin a new one.
      if (wasAwaitingInstallation && (!this.bindBatcher.isEmpty || this.heldVisibleEmit != null)) this.scheduleDrain();
    }
  }

  /** Advance ownership before asynchronous layout preparation, holding content
   * requests until that generation's incoming geometry has been installed. */
  beginBindGeneration(generation: BindGeneration): void {
    this.setBindGeneration(generation);
    if (generation !== this.bindGeneration) return;
    this.pendingBindInstallation = generation;
    this.bindInstallationRevision++;
  }

  /** Release only the matching installation. Requests accumulated during the
   * hold keep their normal coalescing and drain-time occupant resolution. */
  commitBindGeneration(generation: BindGeneration): void {
    if (generation !== this.bindGeneration || this.pendingBindInstallation !== generation) return;
    this.pendingBindInstallation = null;
    // The installation is the geometry: a re-anchor moved the scroll position
    // while it was held, and the rendered columns queued under it still sit at
    // the outgoing range's offsets. Place them now, in the same task as the
    // move, so no frame shows the new position over columns laid out for the
    // old range. Placement is geometry only; their content binds in the drain.
    for (const [index, t] of this.entries._all) {
      if (this.bindBatcher.has(index)) this.placeQueuedColumn(t, index);
    }
    // Visible-range debt kept through a detach resumes here too: the drain
    // delivers it once (an empty batcher drains nothing but still delivers it).
    if (!this.bindBatcher.isEmpty || this.heldVisibleEmit != null) this.scheduleDrain();
  }

  /**
   * The live render epoch. Read-only — consumers (the header-invariant sweep in
   * ScrollHorizontal) need to stamp diagnostics with the generation a violation
   * was observed under; only {@link setBindGeneration} may advance it.
   */
  // The generation at which a drain last reached true quiesce HAVING EXECUTED
  // at least one bind. `hasPendingBinds === false` cannot answer "have the
  // binds I am waiting on run?": the queue is equally empty in the window
  // AFTER a generation bump drops the outgoing instructions and BEFORE the
  // incoming pass enqueues, so a consumer that releases on emptiness alone can
  // be released by work that never happened (the 2026-08-18 reveal-contract
  // hole: a sheet revealed with every scrubbed cell still 0px). Pairing
  // emptiness with this generation makes the two states distinguishable.
  //
  // A drop-only drain does NOT advance it: dropping superseded instructions
  // raises `bindGeneration` without binding anything, so counting it would
  // reopen the same hole one generation later. Thrown binds do not count
  // either: only a delegate that returned supplies successful-bind evidence.
  private _lastBound: LastBound = LastBound.NEVER;
  // Successful binds at `generation` that no quiesce has recorded yet. A drain
  // that completes a generation's last binds while background work (calendar
  // strips on the shared frame budget) is still queued cannot record them —
  // the queue is not idle — and the follow-up drain `onBackgroundIdle`
  // schedules binds nothing, so `afterDrain(prev, 0)` alone would keep the old
  // generation and the sheet's reveal would wait for ever (bug B,
  // repro-viewconfig-slow-retry). The count carries them to that quiesce.
  // It is per generation, so a drop-only generation still counts 0 (L2).
  private unrecordedBinds: { generation: BindGeneration; count: number } = {
    generation: BindGeneration.ZERO, count: 0,
  };
  private indexBindRevision = 0;

  /** @see {@link _lastBound} */
  get lastBound(): LastBound {
    return this._lastBound;
  }

  get currentBindGeneration(): BindGeneration {
    return this.bindGeneration;
  }

  /**
   * The three epoch facts as ONE value — what a consumer deciding whether to
   * release a wait should read. The separate getters remain for the sites that
   * genuinely want a single fact (a breadcrumb stamp), but anything comparing
   * two of them must take a snapshot, because a drain can complete between two
   * reads.
   */
  bindQueue(): BindQueueState {
    return {
      kind: this.hasPendingBinds ? "pending" : "idle",
      current: this.bindGeneration,
      last: this._lastBound,
    };
  }

  // `column.wait` marks (`FrameMarks`, off by default): when each index's
  // pending full bind was first enqueued. Empty while marks are off.
  private readonly fullBindQueuedAt = new Map<number, number>();

  private queueOnBind(t: T, index: number, offsetChangedOnly: boolean = false) {
    if (!offsetChangedOnly && FrameMarks.enabled() && !this.fullBindQueuedAt.has(index)) {
      const at = FrameMarks.stamp();
      this.fullBindQueuedAt.set(index, at);
      // The strip-wait trace follows a fresh calendar column (the engine that
      // runs a content deadline) from here to its first strip unit.
      if (this.delegate?.options?.frameDeadlineMs != null) FrameMarks.StripWait.enqueued(index, at);
    }
    this.bindBatcher.enqueue(index, t, offsetChangedOnly, this.bindGeneration);
    this.scheduleDrain();
  }

  private placeQueuedColumn(t: T, index: number): void {
    const offset = this.window.getOffset(index);
    if (offset == null) return;
    try {
      this.delegate.options?.onPlace?.(t, index, offset, this.cellWidth.getOpt(index));
    } catch (error) {
      // A decorative preview cannot consume the content-bind debt or strand
      // the rest of a detached drain snapshot.
      Tracing.err("ui")(`scroll/bind-drain/onPlace-threw index=${index}: ${String(error)}`);
    }
  }

  private scheduleDrain() {
    if (this.detached) return; // debt is kept; `resume()` schedules it
    if (this.awaitingBindInstallation) return;
    if (this.onBindBatchQueue) return;
    this.onBindBatchQueue = true;
    // Queued binds are frame work: the page's idle lane waits for them (the mark goes stale if no drain renews it).
    FRAME_BUDGET.noteFrameWork(this, true);
    const epoch = this.lifecycle;
    // measureNext, not measure: a drain scheduled from INSIDE the read phase —
    // the budget tail's re-enqueue below, or a queueOnBind fired by an onBind —
    // would join the live read queue and run in the SAME frame (`runTasks`
    // drains the live array), collapsing the whole batch into one long frame
    // and voiding the budget. measureNext lands it in the next frame's read
    // phase; outside a flush the two are equivalent.
    $$.measureNext(() => {
      // Scheduled before a detach: leave everything alone (dispose already
      // cleared the queue flag; a reconnect schedules its own drain).
      if (epoch !== this.lifecycle) return;
      const drainMark = FrameMarks.begin();
      try {
        this.drainBindBatch();
      } finally {
        FrameMarks.end("scroll.drain", drainMark, 0, 0);
      }
    });
  }

  private drainBindBatch() {
    // Reset the queue flag before draining so any queueOnBind triggered during an
    // onBind callback (e.g. height invalidation) starts a fresh batch with its own
    // $$.measure rather than joining this drain (#4/#5).
    this.onBindBatchQueue = false;
    // A previously scheduled frame may arrive after begin. Leave the queue
    // untouched and do not re-arm: commit is the only wake-up while held.
    if (this.awaitingBindInstallation) return;
    const drainGeneration = this.bindGeneration;
    const installationRevision = this.bindInstallationRevision;
    const drainEpoch = this.lifecycle;
    // A delegate callback (onBind, onPlace, a visible fan-out) can detach the
    // owner synchronously; the rest of this snapshot must then neither bind
    // nor place nor complete — it is re-enqueued below like any budget tail.
    const currentInstallation = () => !this.awaitingBindInstallation &&
      this.bindGeneration === drainGeneration &&
      this.bindInstallationRevision === installationRevision &&
      this.lifecycle === drainEpoch && !this.detached;
    const visibleIdx = this.state.visibleIdx;
    // The batcher resolves each queued index to its current occupant (pool swaps
    // between reconciles are bound correctly, not stranded) and forces a full
    // rebind on a swap. See BindBatcher for the sticky/drain rules.
    const instructions = this.bindBatcher.drain(
      (index) => this.entries._all.get(index) ?? null,
    );
    const frameDeadline = this.delegate.options?.frameDeadlineMs?.() ?? null;
    // Offset-only (resize) binds first: they're cheap and keep geometry correct
    // every frame, so under budget pressure content may lag a frame but position
    // never does. Within full binds, paint the live viewport before overscan;
    // stable ties preserve enqueue order, including in the re-enqueued tail.
    instructions.sort((a, b) => {
      const offsetPriority = Number(b.offsetChangedOnly) - Number(a.offsetChangedOnly);
      if (offsetPriority !== 0 || a.offsetChangedOnly) return offsetPriority;
      const visiblePriority = Number(NumRangeLike.contains(visibleIdx, b.index)) -
        Number(NumRangeLike.contains(visibleIdx, a.index));
      if (visiblePriority !== 0 || frameDeadline == null) return visiblePriority;
      const edge = this.scrollDirection < 0 ? visibleIdx.start : visibleIdx.end;
      return Math.abs(a.index - edge) - Math.abs(b.index - edge);
    });
    const drainStart = performance.now();
    const budgetMs = ScrollEngine.DRAIN_BUDGET_MS;
    if (frameDeadline != null) {
      const lastMove = Math.max(this.lastUserActivityAt, this.lastCrossAxisActivityAt);
      FRAME_BUDGET.setContentDeadline(
        drainStart - lastMove < FrameBudget.MOTION_QUIET_MS ? frameDeadline : ScrollEngine.DRAIN_BUDGET_MS,
      );
    }
    if (this.heldVisibleEmit != null && currentInstallation()) {
      const source = this.heldVisibleEmit;
      this.heldVisibleEmit = null;
      this.emit.visibleRange(this.state.visibleIdx, source);
    }
    let done = 0;
    let droppedStale = 0;
    let successfulBinds = 0;
    let fullBinds = 0;
    // Whether this drain ran the frame's unconditional unit (the strip-wait trace).
    let tookFirstUnit = false;
    // Which indices were dropped this drain — UNCAPPED, because every one of
    // them is re-checked for recovery after the loop (the log slices it).
    const droppedIndices: number[] = [];
    for (; done < instructions.length; done++) {
      // A delegate can synchronously begin (and even commit) a successor.
      // Its installation cannot borrow the rest of this detached snapshot.
      if (!currentInstallation()) break;
      // Forward-progress guard: always bind at least one per frame so a single
      // expensive cell can't starve the queue.
      if (
        done > 0 &&
        (frameDeadline != null
          ? FRAME_BUDGET.remainingMs() <= 0
          : performance.now() - drainStart > budgetMs)
      ) {
        break;
      }
      // This frame's unconditional unit is the engine's: a later budgeted
      // queue checks the deadline before its first unit.
      if (frameDeadline != null) {
        FRAME_BUDGET.takeFirstUnit();
        tookFirstUnit = true;
      }
      const { index, t, offsetChangedOnly, generation } = instructions[done];
      // Interruptible rendering: this bind's content belongs to a superseded
      // render, so executing it would paint a sheet the user has navigated away
      // from — drop it. But the INDEX may still be occupied by the winning
      // render (a switch racing a scroll re-marks the same columns), and the
      // mark pass has already set `pending-layout`/`recycled` on the occupant,
      // whose only clear is a completed bind. So a drop is not a skip — every
      // dropped index is re-checked after the loop and re-emitted at the live
      // generation if still rendered (see the recovery pass below). Inert when
      // bindGeneration is 0 (feature off): `0 < 0` is false. Counts toward
      // `done` so the deferred-tail math and onBatchComplete stay correct.
      if (BindGeneration.gate(generation, this.bindGeneration) === "drop-stale") {
        droppedStale++;
        droppedIndices.push(index);
        continue;
      }
      // A single column's onBind throwing MUST NOT abort the whole drain: that
      // would leave the throwing cell AND every queued cell after it rendered
      // but unbound (empty `cid`), and skip the deferred-tail re-enqueue +
      // onBatchComplete below — exactly the `sheet/recycling/mismatch` strand
      // storm seen on resize/navigation when a mid-rebuild alias transiently
      // violates the "row is cell-bindable" contract. Swallow per-cell so the
      // loop keeps draining; the unbound cell self-heals on the next emit
      // (assertChildren re-enqueues it). Never propagate an Err into this loop.
      if (!offsetChangedOnly && this.fullBindQueuedAt.size > 0) {
        const queuedAt = this.fullBindQueuedAt.get(index);
        if (queuedAt != null) {
          this.fullBindQueuedAt.delete(index);
          FrameMarks.latency("column.wait", queuedAt, index, 0, index);
        }
      }
      try {
        this.delegate!.onBind(
          t,
          index,
          this.window.getOffset(index)!,
          this.cellWidth.getOpt(index),
          offsetChangedOnly,
          !NumRangeLike.contains(visibleIdx, index),
        );
        successfulBinds += 1;
        if (!offsetChangedOnly) fullBinds += 1;
      } catch (error) {
        Tracing.err("ui")(
          `scroll/bind-drain/onBind-threw index=${index}: ${String(error)}`,
        );
      }
    }
    // Charge the shared frame budget with this drain: background work
    // (calendar strips) runs against what is left.
    if (successfulBinds > 0) {
      if (this.unrecordedBinds.generation !== drainGeneration) {
        this.unrecordedBinds = { generation: drainGeneration, count: 0 };
      }
      this.unrecordedBinds.count += successfulBinds;
    }
    FRAME_BUDGET.charge(performance.now() - drainStart);
    if (frameDeadline != null && FrameMarks.enabled()) {
      this.noteStripWait(tookFirstUnit, instructions.slice(done));
    }
    const tail = instructions.length - done;
    const deferred = tail;
    // Trace hook `scroll/bind-pace` (recorded only while the budget records pace):
    // the full binds this drain had, ran and left for a later frame.
    let fullDeferred = 0;
    for (let i = done; i < instructions.length; i++) if (!instructions[i].offsetChangedOnly) fullDeferred++;
    FRAME_BUDGET.notePace("binds", fullBinds + fullDeferred, fullBinds, fullDeferred);
    FRAME_BUDGET.noteFrameWork(this, deferred > 0);
    if (deferred > 0) {
      // Re-enqueue the unbound tail; the next drain
      // re-resolves occupants and drops any index no longer rendered.
      // Generation rides along so a tail that is superseded before the next
      // drain still gets dropped. Each keeps its kind (sticky enqueue) and
      // its live occupant resolution.
      for (let i = done; i < instructions.length; i++) {
        const ins = instructions[i];
        this.bindBatcher.enqueue(ins.index, ins.t, ins.offsetChangedOnly, ins.generation);
      }
      // What is left drains next frame (a no-op while detached; `resume()`
      // schedules the kept debt).
      this.scheduleDrain();
    }
    if (droppedStale > 0) {
      // RECOVERY: a dropped index whose slot is still rendered belongs to the
      // WINNING render — the mark pass hid it (`pending-layout`) and only a
      // completed bind un-hides it, so dropping its bind outright is the
      // permanently-blank-header-column bug. Re-emit through `emit.index`: it
      // resolves the CURRENT occupant (drain-time resolution, so pool swaps
      // bind the right element), enqueues a FULL bind stamped with the LIVE
      // generation (immune to this same drop unless superseded again — in
      // which case that drain recovers it again; converges when switching
      // stops), and no-ops for indices the winning render no longer shows.
      const recovered: number[] = [];
      for (const index of droppedIndices) {
        if (this.entries.get(index) == null) continue;
        recovered.push(index);
        this.emit.index(index);
      }
      // Trace hook `bind/drop-stale`: the superseded binds this drain dropped.
      // `liveGen` is THIS engine's `BindGeneration` wearing fastdom's field
      // name — the two counters are unrelated and `fastdom.liveGen` is on its
      // own deletion path. The key stays because recorded traces carry it; the
      // name is the only thing about it that was ever shared.
      Tracing.log("bind/drop-stale", {
        dropped: droppedStale,
        liveGen: this.bindGeneration,
      });
      // DIAG(header-blank): which indices were dropped and which of them were
      // re-emitted at the live generation. `recovered` non-empty is the bug
      // path healing itself; the header-invariant sweep stays the tripwire for
      // anything this recovery still misses.
      Tracing.log("scroll/bind-drop-stale", {
        count: droppedStale,
        indices: droppedIndices.slice(0, 20),
        recovered: recovered.slice(0, 20),
        recoveredCount: recovered.length,
        bindGen: this.bindGeneration,
      });
    }
    if (done > 0) {
      Tracing.dev(
        `scroll/bind-drain done=${done} deferred=${deferred} dropped=${droppedStale} ms=${
          Math.round((performance.now() - drainStart) * 10) / 10
        }`,
      );
    }
    // Signal completion only when the whole queue has drained — the consumer's
    // assertChildren() would false-positive on a partially-bound frame, and
    // `whenSettled()` waiters take this callback as "every rendered cell holds
    // its new content". The drop-recovery re-emits above and any coverage
    // repair below re-enqueue binds, so completion is handed to the follow-up
    // drain in that case: its own quiesce re-runs this check with the queue
    // truly empty and fires the callback then. The chain terminates — repair
    // attempts are latched and capped, and recovery re-emits converge when
    // switching stops.
    if (deferred === 0 && currentInstallation()) {
      this.enforceViewportCoverage();
      // Only background work is left: complete when it drains.
      if (currentInstallation() && FRAME_BUDGET.hasBackgroundDebt() &&
        this.bindBatcher.isEmpty && !this.awaitingBindInstallation) {
        FRAME_BUDGET.whenBackgroundIdle(this.onBackgroundIdle);
      }
      if (currentInstallation() && !this.hasPendingBinds) {
        // Record BEFORE the callback: a settle waiter woken synchronously by
        // onBatchComplete must observe the generation this quiesce reached.
        // Only a drain that successfully completed a bind counts — see
        // `_lastBound`.
        // Every successful bind of this generation counts, including those of
        // an earlier drain that could not quiesce (see `unrecordedBinds`).
        const executed = this.unrecordedBinds.generation === drainGeneration
          ? this.unrecordedBinds.count : 0;
        this.unrecordedBinds = { generation: drainGeneration, count: 0 };
        this._lastBound = LastBound.afterDrain(
          this._lastBound,
          executed,
          drainGeneration,
        );
        this.delegate?.options?.onBatchComplete?.();
      }
    }
  }

  /**
   * Tell the strip-wait trace (`FrameMarks.StripWait`, marks on) what this
   * drain did: whether it ran the frame's unconditional unit, and which full
   * binds it kept queued and why.
   */
  private noteStripWait(
    tookFirstUnit: boolean,
    tail: readonly { index: number; offsetChangedOnly: boolean }[],
  ): void {
    const kept: [number, FrameMarks.StripWait.Deferral][] = [];
    for (const ins of tail) if (!ins.offsetChangedOnly) kept.push([ins.index, "deadline"]);
    FrameMarks.StripWait.drained($$.frameNumber(), FRAME_BUDGET.inMotion(), tookFirstUnit, kept);
  }

  // The coverage-repair EPISODE — null IS the healthy (Covered) state, so the
  // per-state latch, the once-per-state stuck report, and the per-episode
  // attempt budget reset in one assignment and can never fall out of step.
  //
  // attemptedKey — one repair attempt per stuck state, keyed by (generation,
  // visible pixel range) rather than generation alone, so a session with
  // interruptible rendering off (generation pinned at 0) still repairs each
  // NEW stranding.
  // reportedKey — the stuck report fires ONCE per stuck state, not once per
  // quiesce, so a persistent failure is a diagnosable line, not a stream.
  // attempts — the categorical cap's counter. The per-state latch cannot stop
  // an ALTERNATING sequence of stuck states (a silent offset flap makes every
  // quiesce a "new" state, each earning an attempt); the cap can. Dies with
  // the episode, so a long healthy session never exhausts it.
  private coverageEpisode: Option<CoverageEpisode> = null;
  private static readonly COVERAGE_REPAIR_CAP = 4;

  /**
   * VIEWPORT-COVERAGE INVARIANT, enforced at every batch quiesce (a drain
   * that emptied the queue): the pixel strip under the live viewport is
   * covered by the rendered entries' pixel span.
   *
   * The state this catches is a silent re-anchor race: a navigation rebuilds
   * the window around a new anchor while the scrollable still sits over a
   * region the new window never rendered — and because the offset was moved
   * programmatically (or not at all), no scroll event arrives to reconcile
   * it. The strip under the user is populated only by leftover pool columns
   * no entry addresses: not the drain's stale-drop recovery (it re-emits by
   * index, and there is no entry at those indices), not assertChildren (it
   * iterates entries). Without this check the viewport stays blank until a
   * lucky scroll.
   *
   * The check compares PIXEL spans, not index ranges: in the stranded state
   * the window map does not extend under the live offset, so an index-space
   * comparison clamps to the map's edge and reads "covered" across an
   * arbitrarily wide hole. The visible offset range is derived from the raw
   * content offset and is truthful everywhere.
   *
   * The repair is exactly the missing scroll event, run by hand: a reconcile
   * against the live viewport. `reconcile` itself extends the window map
   * under the live offset (its opening `extendToOffsetRange` covers the
   * rendered pixel range), re-derives the rendered index range there, and
   * the transaction removes the stranded far-away entries and adds + binds
   * the strip under the user. No scroll position is touched, no target index
   * needs to resolve — which matters, because inside the hole
   * `visible.indexRange` cannot name one. One attempt per stuck state
   * ({@link coverageEpisode}); a repair that does not take is reported,
   * never retried — the DOM-truth sweep in ScrollHorizontal stays the
   * independent tripwire for that.
   */
  private enforceViewportCoverage(): void {
    const viewport = this._viewport;
    const cellPx = this.cellWidth._default;
    if (viewport == null || cellPx == null || cellPx <= 0) return;

    const visible = viewport.visible.offsetRange;
    const uncoveredPx = this.viewportUncoveredPx(visible);
    // Sub-cell gaps are boundary jitter (fractional strides, rubber-banding
    // overscroll); a real stranding is at least a column wide. The tolerance
    // is the NARROWEST local cell (edge cells of the rendered strip), not the
    // default width — under variable-width geometry a multi-cell gap of
    // narrow cells must not pass as jitter of one wide default cell.
    const tolerancePx = this.coverageTolerancePx(cellPx);
    if (uncoveredPx < tolerancePx) {
      this.coverageEpisode = null;
      return;
    }
    const key = `${this.bindGeneration}:${Math.round(visible.start)}:${
      Math.round(visible.end)
    }`;
    const episode = this.coverageEpisode ??= {
      attemptedKey: null,
      reportedKey: null,
      attempts: 0,
    };
    if (episode.attemptedKey === key) {
      // The one allowed attempt for this stuck state already ran and did not
      // take — a structural bug above this invariant. Report, never loop.
      this.reportCoverageStuck(episode, key, uncoveredPx);
      return;
    }
    if (episode.attempts >= ScrollEngine.COVERAGE_REPAIR_CAP) {
      // A DIFFERENT stuck state, but this failure episode already spent its
      // attempt budget — an alternating-state flap must not turn quiesces
      // into a reconcile treadmill. Report; a real scroll or navigation is
      // the only way forward from here.
      this.reportCoverageStuck(episode, key, uncoveredPx);
      return;
    }
    episode.attemptedKey = key;
    episode.attempts += 1;
    Tracing.log("scroll/coverage-repair", {
      gen: this.bindGeneration,
      uncoveredPx: Math.round(uncoveredPx),
    });
    Tracing.info("ui")(
      `scroll/coverage-repair gen=${this.bindGeneration} uncoveredPx=${
        Math.round(uncoveredPx)
      }`,
    );
    const transaction = this.state.reconcile(viewport);
    this.applyTransaction(transaction, "coverage-repair");
    // Re-check SYNCHRONOUSLY: if the one attempt did not take, this is the
    // only guaranteed chance to say so. In a bounded index space a viewport
    // stranded fully outside [minIndex, maxIndex] clamps the reconcile to the
    // INVALID range — the transaction recycles every entry, and with nothing
    // left to bind no future drain (and so no future quiesce) ever runs the
    // deferred same-key report above. Report at the moment of failure; the
    // latch still holds, so a stuck state that KEEPS quiescing (partial
    // overlap with content) never re-repairs.
    const residualPx = this.viewportUncoveredPx(viewport.visible.offsetRange);
    if (residualPx >= tolerancePx) {
      this.reportCoverageStuck(episode, key, residualPx);
    }
  }

  /** One `scroll/coverage-repair/stuck` line per stuck state, not per quiesce. */
  private reportCoverageStuck(
    episode: CoverageEpisode,
    key: string,
    uncoveredPx: number,
  ): void {
    if (episode.reportedKey === key) return;
    episode.reportedKey = key;
    Tracing.err("ui")(
      `scroll/coverage-repair/stuck gen=${this.bindGeneration} uncoveredPx=${
        Math.round(uncoveredPx)
      } attempts=${episode.attempts}`,
    );
  }

  /**
   * The jitter tolerance in LOCAL cell units: the narrowest of the default
   * width and the rendered strip's edge cells. Floored at 1px so degenerate
   * zero-width cells cannot turn boundary jitter into a repair storm.
   */
  private coverageTolerancePx(defaultPx: number): number {
    const rendered = this.state.renderedIdx;
    if (!Number.isFinite(rendered.start) || rendered.end < rendered.start) {
      return defaultPx;
    }
    return Math.max(
      1,
      Math.min(
        defaultPx,
        this.cellWidth.get(rendered.start),
        this.cellWidth.get(rendered.end),
      ),
    );
  }

  /**
   * Pixels of the visible strip NOT covered by the rendered entries' pixel
   * span; +Infinity when nothing is rendered or the rendered ends are
   * unmapped. Pixel spans, not index ranges — see
   * {@link enforceViewportCoverage}.
   *
   * A list SHORTER than the viewport is not a stranding. When the bounded
   * content ([minIndex, maxIndex]) is shorter than the visible strip, no
   * scroll position can fill the strip: `visible - content` pixels of it lie
   * outside the content wherever the view sits. That much of the gap outside
   * the content is excused ({@link unavoidableOutsidePx}); the rest is still
   * counted. So a 3-message chat thread in a 314px pane, bound at the 24px
   * estimate before its first measure (72px of content, a 242px tail), is
   * covered; while a long list whose view was left past its end (the stress
   * battery's PARTIAL and DISJOINT strandings: 2100px of content under a
   * 1460px strip) has no slack and still reports.
   */
  private viewportUncoveredPx(visible: NumberRange): number {
    const rendered = this.state.renderedIdx;
    if (!Number.isFinite(rendered.start) || rendered.end < rendered.start) {
      return Number.POSITIVE_INFINITY;
    }
    const renderedStartPx = this.window.getOffset(rendered.start);
    const renderedEndPx = this.endPxOf(rendered.end);
    if (renderedStartPx == null || renderedEndPx == null) {
      return Number.POSITIVE_INFINITY;
    }
    let headPx = Math.max(0, renderedStartPx - visible.start);
    let tailPx = Math.max(0, visible.end - renderedEndPx);
    const content = this.boundedContentPx();
    if (content != null) {
      let slackPx = Math.max(
        0,
        (visible.end - visible.start) - (content.end - content.start),
      );
      // The part of each gap that lies outside the content, excused up to the
      // slack in total. A gap INSIDE the content (rendered entries missing
      // under the view) is never excused.
      const headOutsidePx = Math.min(
        headPx,
        Math.max(0, content.start - visible.start),
      );
      const headExcusedPx = Math.min(headOutsidePx, slackPx);
      headPx -= headExcusedPx;
      slackPx -= headExcusedPx;
      const tailOutsidePx = Math.min(
        tailPx,
        Math.max(0, visible.end - content.end),
      );
      tailPx -= Math.min(tailOutsidePx, slackPx);
    }
    return Math.max(0, headPx, tailPx);
  }

  /** The far pixel edge of `index`: the offset past it, else its start plus
   *  its width; null when the window does not map it. */
  private endPxOf(index: number): Option<number> {
    const pastPx = this.window.getOffset(index + 1);
    if (pastPx != null) return pastPx;
    const startPx = this.window.getOffset(index);
    return startPx != null ? startPx + this.cellWidth.get(index) : null;
  }

  /**
   * The pixel extent of the whole content when the layout bounds it at BOTH
   * ends and the window maps both ends; null otherwise (an unbounded sheet
   * has no end to be past, and an unmapped end cannot be measured — the
   * check then counts every uncovered pixel, as before).
   */
  private boundedContentPx(): Option<NumberRange> {
    const props = this._viewport?.props;
    const minIndex = props?.minIndex;
    const maxIndex = props?.maxIndex;
    if (minIndex == null || maxIndex == null || maxIndex < minIndex) {
      return null;
    }
    const startPx = this.window.getOffset(minIndex);
    const endPx = this.endPxOf(maxIndex);
    if (startPx == null || endPx == null) return null;
    return new NumberRange(startPx, endPx);
  }
}

/**
 * The viewport-coverage repair episode — the single record behind the
 * quiesce-repair discipline in `enforceViewportCoverage`. Field semantics
 * live at the `coverageEpisode` declaration.
 */
interface CoverageEpisode {
  attemptedKey: Option<string>;
  reportedKey: Option<string>;
  attempts: number;
}

export namespace ScrollEngine {
  export type ViewportChangeSource =
    | "bind"
    | "layout"
    | "navigator"
    | "scroll"
    | "snap";

  /**
   * How a bind that MOVES the view presents the move. These are presentation
   * details of the move itself, so they live only on the two {@link IndexBind}
   * kinds that move — on a `refresh` or a `reconcile` there is no scroll to
   * present and the fields would be decoration a reader has to disprove.
   *
   * Every field is required. `position`/`scrollBehavior`/`useNativeScrollTo`
   * were the three `undefined`-defaulted members of the retired
   * `BindIndexOptions` (`"start"` / `"auto"` / `false`); an omitted one read
   * the same whether the caller wanted the default or had not considered the
   * question.
   */
  export interface RevealAt {
    /** Where in the viewport `index` lands. */
    readonly position: "start" | "center" | "end";
    /** Animation for the scroll itself. `"auto"` was the old default. */
    readonly scrollBehavior: ScrollBehavior;
    /**
     * Use the platform `Element.scrollTo` rather than the engine's own
     * animated path. `false` was the old default.
     */
    readonly useNativeScrollTo: boolean;
  }

  /**
   * WHY the caller is binding an index — the three questions the retired
   * `BindIndexOptions` answered by omission, as one positive claim.
   *
   * `bindIndex` answers exactly three things, and the old two-boolean bag
   * (`contextDirtied`, `skipScroll`) crossed them at right angles:
   *
   * ```text
   *              RESET the map   MOVE the view   EMIT `all`
   *   reanchor         yes            yes           yes      contextDirtied, !skipScroll
   *   reveal            no            yes            no     !contextDirtied, !skipScroll
   *   refresh           no             no           yes      contextDirtied,  skipScroll
   *   reconcile         no             no            no     !contextDirtied,  skipScroll
   * ```
   *
   * `skipScroll` gated BOTH the re-origin and the move, so a caller that
   * wanted one without the other could not say so, and `skipScroll: undefined`
   * silently meant "re-origin the map AND move the user". There is no default
   * here: a caller that has not decided which of these it is has no way to
   * express that by leaving a field out.
   *
   * This is the same idiom as {@link IndexSpace}, one method over: a positive
   * claim in place of a decision expressed by omission.
   */
  export type IndexBind =
    /**
     * A sheet or timescale change: the index space itself moved, so re-origin
     * the index↔pixel map, move the view to `index`, and re-emit `all` so
     * every rendered entry rebinds its content. The destructive one — the only
     * kind that resets the window.
     */
    | {
      readonly kind: "reanchor";
      readonly at: RevealAt;
      readonly viewportChangeSource: ViewportChangeSource;
    }
    /**
     * A navigator jump, a snap settle, a scroll-to-tail: move the view to
     * `index` and keep the map. Content is not dirtied — whatever the move
     * brings into the rendered range rebinds through the reconcile, and
     * nothing else does.
     */
    | {
      readonly kind: "reveal";
      readonly at: RevealAt;
      readonly viewportChangeSource: ViewportChangeSource;
    }
    /**
     * A data-only refresh at the index the user is already on: rebind the
     * content in place. Neither re-origins the map nor moves the view —
     * resetting the window here would zero `offsetBase` and snap the view for
     * a change that did not move anything.
     */
    | {
      readonly kind: "refresh";
      readonly viewportChangeSource: ViewportChangeSource;
    }
    /**
     * Neither re-origin, nor move, nor re-emit: extend the window around
     * `index`, reconcile, and adopt `index` as the optimistic anchor. The
     * quietest kind — a bind that only restates where the view already is
     * (a legend-only rebind under a `keep` anchor).
     */
    | {
      readonly kind: "reconcile";
      readonly viewportChangeSource: ViewportChangeSource;
    };

  /**
   * What {@link ScrollEngine.bindIndex} actually did, in the same three terms
   * the kind promises — a bind that says so is a bind a caller can reason about
   * without re-deriving it from the arguments it passed.
   */
  export interface IndexBindResult {
    /** The index↔pixel map was re-origined (`ScrollWindow.reset`). */
    readonly reanchored: boolean;
    /** The scrollable was told to move. */
    readonly moved: boolean;
    /** Every rendered entry was re-emitted (`emit.all`). */
    readonly emitted: boolean;
  }

  export type LayoutProps = ScrollViewport.Props & {
    cellWidthPx: number;
    maxIndex?: Option<number>;
  };

  /**
   * What one column's CONTENT is, as the space that minted it names it: a date
   * for a sheet, the index itself for a plain list. Opaque to the engine, which
   * never reads a key — it takes one out of the outgoing space and hands it
   * straight to the incoming one. Named (rather than `unknown`) so the erased
   * form below is a type a reader can look up.
   */
  export type ContentKey = object | number | string;

  /**
   * An index space: what an engine index MEANS.
   *
   * `key` is the identity the engine compares, by REFERENCE — two spaces with
   * the same `key` are the same space, and nothing else about them is inspected.
   * `keyAt`/`indexOf` are how one column's content crosses between spaces:
   * `incoming.indexOf(outgoing.keyAt(i))` is "the index that now shows what
   * index `i` used to show". Generic in the content key so the engine stays
   * ignorant of dates; see {@link ScrollEngine.bindLayout}'s third ordering for
   * why it holds this rather than a caller-supplied translation.
   */
  export interface IndexSpace<K extends ContentKey> {
    readonly key: object;
    keyAt(index: number): K;
    indexOf(key: K): number;
    /**
     * Which indices this space can address at all —
     * {@link ScrollWindow.Reach}.
     *
     * It belongs here for the same reason `keyAt` does: "index −40330 is the
     * year 1253" and "the year 1253 is not on this calendar" are the same fact
     * about the same mapping, and splitting them across two owners is how they
     * came to disagree. The scroll strip is a fixed 16.7M px with its origin at
     * the middle, so at a week column's ~200px its left edge is 40 thousand
     * columns — eight centuries — before today: a scrollbar dragged to the end
     * showed real columns with nothing behind them (R20/F5). The engine never
     * reads a bound's meaning, only its number.
     *
     * A method rather than a field because the band moves with the clock and a
     * space outlives a day.
     */
    reach(): ScrollWindow.Reach;
  }

  export namespace IndexSpace {
    /**
     * A space at the content-key type the engine does not know. Every
     * `IndexSpace<K>` widens to this — `K` is a return type on `keyAt` and a
     * method parameter on `indexOf`, so nothing here has to be cast — which is
     * what lets one engine hold spaces of any key type in one field and still
     * pair `keyAt` with `indexOf` across a flip.
     */
    export type Erased = IndexSpace<ContentKey>;

    /**
     * Indices ARE the content keys: index 3 means row 3 and nothing else, so no
     * rebind can change what one means. The claim a consumer with no mapper
     * makes (a vertical list) — the positive form of "this is not a question I
     * have", and the successor to the retired `AnchorTranslate.sameSpace()`.
     *
     * One singleton, so `identity().key === identity().key`: two rebinds of a
     * list are never a space change.
     */
    const IDENTITY: IndexSpace<number> = {
      key: {},
      keyAt: (index) => index,
      indexOf: (key) => key,
      // Every index means a row, and a row is as good as any other row: there
      // is no axis to run off. What bounds a list is its content length, which
      // the engine already carries as `LayoutProps.maxIndex`.
      reach: () => ScrollWindow.Reach.UNBOUNDED,
    };

    /** @see {@link IDENTITY} */
    export function identity(): IndexSpace<number> {
      return IDENTITY;
    }
  }

  /**
   * The largest index magnitude {@link ScrollEngine.bindLayout} will re-anchor
   * to. `ScrollWindow.extendToIdxRange` walks one index at a time into a dense
   * array subscripted by engine index, so the cost of reaching an anchor is
   * linear in its magnitude and its storage is an array that long — a caller's
   * arithmetic error is therefore paid in main-thread seconds and then a
   * `RangeError`, not in a wrong column.
   *
   * The number itself lives on {@link ScrollWindow.MAX_REANCHOR_SPAN}, with the
   * array that pays for it. Two layers screen the same arithmetic — this one
   * rejects a translated ANCHOR before the window is asked to reach it, the
   * window refuses any walk at all — and they have to agree, so there is one
   * constant and this is its alias.
   */
  export const MAX_REANCHOR_SPAN = ScrollWindow.MAX_REANCHOR_SPAN;

  /**
   * Whether a translated anchor is something the window can actually be moved
   * to. Non-integers are rejected as malformed rather than rounded: an index is
   * a subscript, and a translation that produced 3.5 has a bug the engine should
   * not paper over — it would silently fail to reposition anyway, because the
   * offset lookup for a fractional index misses.
   */
  export function isAddressableAnchor(index: Option<number>): boolean {
    return index != null &&
      Number.isInteger(index) &&
      Math.abs(index) <= MAX_REANCHOR_SPAN;
  }

  export type Delegate<T> = {
    onCreate: () => T;
    onBind: (
      $e: T,
      index: number,
      position: number,
      widthPx: Option<number>,
      offsetChangedOnly: boolean,
      isOffscreen: boolean,
    ) => void;
    onRecycle: ($e: T) => void;
    options?: Option<OptionalCallbacks<T>>;
  };

  export type OptionalCallbacks<T> = Optional<{
    onRenderedIndiciesWillChange: (r: NumRangeLike) => void;
    onRenderedIndiciesChanged: (r: NumRangeLike) => void;
    onVisibleIndiciesChanged: (
      r: NumRangeLike,
      viewportChangeSource: ViewportChangeSource,
    ) => void;
    cellWidthForIndex: (index: number) => Option<number>;
    onBatchComplete: () => void;
    onNegativeOverflow: (overflow: number) => void;
    /**
     * `scroll-frame-deadline`: the content budget (ms after the frame opened)
     * while either axis is moving; null = off. On: the drain and the strip
     * queue share one wall-clock deadline per frame, full binds sort visible
     * first then by distance in the direction of travel.
     */
    frameDeadlineMs?: () => Option<number>;
    /**
     * `scroll-column-retention`: how many recycled cells the pool keeps keyed
     * by the index they last held, so a cell returning to that index is the
     * one that showed it (see `Pool`); 0, null or absent: the LIFO pool.
     */
    columnRetention?: () => number;
    /**
     * Placement without content, for a rendered column whose full bind is
     * still queued when a bind installation commits (`commitBindGeneration`):
     * give it its live index, width and offset so it sits at the new position
     * (still unbound, still hidden by its `recycled` / `pending-layout` marks)
     * instead of at the outgoing range's offsets until its bind. Must not
     * claim the column as bound; the adapter skips columns that already carry
     * live geometry.
     */
    onPlace: (
      $e: T,
      index: number,
      position: number,
      widthPx: Option<number>,
    ) => void;
  }>;

  export function calculateRenderedDelta(
    currentRange: NumRangeLike,
    newRange: NumRangeLike,
  ): { start: number; end: number } {
    return {
      start: newRange.start - currentRange.start,
      end: newRange.end - currentRange.end,
    };
  }

  export function validateIndexRange(
    range: NumOptRange,
    minCount: number = 0,
  ): NumOptRange {
    if (range.start == null || range.end == null) return range;
    const count = range.end - range.start + 1;
    if (count < minCount) {
      const additionalNeeded = minCount - count;
      const startAdjust = Math.floor(additionalNeeded / 2);
      return {
        start: Math.max(0, range.start - startAdjust),
        end: range.end + (additionalNeeded - startAdjust),
      };
    }
    return range;
  }

  /**
   * The engine's spare cells.
   *
   * Without retention (`retention` null or answering 0) the pool is a LIFO
   * stack: a transaction recycles the cells leaving the rendered strip before
   * it adds the entering ones, so every leaving cell is handed straight to an
   * entering index (today's behaviour, byte for byte).
   *
   * With retention K > 0 (`scroll-column-retention`) a recycled
   * cell is kept keyed by the index it last held, and `get(index, near)`
   * prefers, in order: the spare keyed by `index` (a hit: the cell still holds
   * that index's content, so its delegate bind can reuse it); an unkeyed spare;
   * a new cell from the factory while the pool holds no more than K spares (it
   * grows to K once); else the keyed spare farthest from `near` (evicted and
   * retargeted). `forget` unkeys every spare (the index -> content map moved).
   */
  export class Pool<T> {
    private factory: () => T;
    private pool: T[] = [];
    readonly #retention: Option<() => number>;
    /** Retained spares by the index they last held (retention only). */
    readonly #keyed = new Map<number, T>();
    #stats: Pool.Stats = { hits: 0, retargets: 0, created: 0 };

    constructor(factory: () => T, retention: Option<() => number>) {
      this.factory = factory;
      this.#retention = retention;
    }

    #limit(): number {
      return Math.max(0, this.#retention?.() ?? 0);
    }

    get(index: number, near: NumRangeLike): T {
      if (this.#limit() <= 0 && this.#keyed.size === 0) {
        const r = this.pool.pop();
        if (r) {
          this.#stats.retargets++;
          return r;
        }
        this.#stats.created++;
        return this.factory();
      }
      const kept = this.#keyed.get(index);
      if (kept != null) {
        this.#keyed.delete(index);
        this.#stats.hits++;
        return kept;
      }
      const spare = this.pool.pop();
      if (spare) {
        this.#stats.retargets++;
        return spare;
      }
      if (this.#keyed.size <= this.#limit()) {
        this.#stats.created++;
        return this.factory();
      }
      let far: Option<number> = null;
      let farDistance = -1;
      for (const key of this.#keyed.keys()) {
        const distance = key < near.start ? near.start - key : key > near.end ? key - near.end : 0;
        if (distance > farDistance) {
          far = key;
          farDistance = distance;
        }
      }
      const evicted = nonnull(this.#keyed.get(nonnull(far)));
      this.#keyed.delete(nonnull(far));
      this.#stats.retargets++;
      return evicted;
    }

    recycle(t: T, index: number) {
      if (this.#limit() > 0 && !this.#keyed.has(index)) this.#keyed.set(index, t);
      else this.pool.push(t);
    }

    /** Unkey every retained spare: the content at each index is no longer known (a re-anchor, a layout rebuild). */
    forget(): void {
      for (const t of this.#keyed.values()) this.pool.push(t);
      this.#keyed.clear();
    }

    /** Hits, retargets and creations since the last call, and the spares held now. */
    takeStats(): Pool.Stats & { spares: number } {
      const stats = { ...this.#stats, spares: this.size() };
      this.#stats = { hits: 0, retargets: 0, created: 0 };
      return stats;
    }

    /** Empty the pool, returning the spares so the caller can dispose them. */
    drain(): T[] {
      const drained = [...this.pool, ...this.#keyed.values()];
      this.pool = [];
      this.#keyed.clear();
      return drained;
    }

    size(): number {
      return this.pool.length + this.#keyed.size;
    }
  }

  export namespace Pool {
    /** How the pool answered `get` (see `Pool`). */
    export type Stats = { hits: number; retargets: number; created: number };
  }

  export type Transaction = {
    renderedChanged: boolean;
    visibleChanged: boolean;
    changeset: {
      adds: NumberRange[];
      removes: NumberRange[];
    };
  };

  export class State {
    private _layoutDirtied: boolean = false;
    private _contextDirtied: boolean = false;

    renderedIdx: NumberRange = NumberRange.INVALID;
    visibleIdx: NumberRange = NumberRange.INVALID;

    constructor(readonly window: ScrollWindow) {}

    get layoutDirtied(): boolean {
      return this._layoutDirtied;
    }

    markLayoutDirtied() {
      this._layoutDirtied = true;
      this.window.reset("layout");
    }

    // `reanchor` is true only when the caller is repositioning scroll (a
    // sheet/timescale change). A content refresh on the same sheet leaves cell
    // widths unchanged, so the offset window stays valid; resetting it there
    // would zero offsetBase and snap the view. Always mark content dirty (forces
    // a full re-add in reconcile); reset the window only on a genuine re-anchor.
    markContextDirtied(reanchor: boolean) {
      this._contextDirtied = true;
      if (reanchor) this.window.reset("context-reanchor");
    }

    /**
     * Re-derive the visible and rendered index ranges from where the scroller
     * actually is, and hand back the cells that have to move.
     *
     * The opening extend is what makes the rest of this method answerable: it
     * grows the index↔pixel map under the live offset so the two `viewport`
     * queries below have something to resolve against. That walk can come up
     * SHORT — a stalled run of non-positive widths, or the span ceiling — and
     * when it does, every range derived here is a statement about pixels the
     * map does not cover. That used to be settled after the fact and wrongly:
     * the walk broke silently, the lookups answered `null`, and `clamp` and
     * `changeset` ran on a `NumberRange` typed `number` and holding nothing.
     *
     * So the outcome is judged HERE, before a single range is asked for. There
     * is no repair available at this altitude — a delegate answering 0 for
     * every width is not something the reconcile can fix — so what it does is
     * name the state, and then proceed knowing the ranges are read against the
     * span the window REACHED ({@link ScrollWindow.findIndiciesFromOffsetRange}
     * clamps into it, and cannot answer with a null). The strip that is left
     * uncovered is `enforceViewportCoverage`'s to report and retry.
     */
    reconcile(viewport: ScrollViewport): Transaction {
      const requested = viewport.rendered.offsetRange;
      const extend = this.window.extendToOffsetRange(requested);
      if (extend.kind === "short") this.reportShortExtend(extend);

      const layoutDirty = this._layoutDirtied;
      const contextDirty = this._contextDirtied;
      const dirtied = layoutDirty || contextDirty;
      const { minIndex, maxIndex } = viewport.props;

      const visible = viewport.visible.state;
      const clampedVisibleIdx = visible.index.clamp(minIndex, maxIndex);
      const visibleChanged =
        !clampedVisibleIdx.equals(this.visibleIdx) || dirtied;
      this.visibleIdx = clampedVisibleIdx;

      const rendered = viewport.rendered.state;
      const clampedRenderedIdx = rendered.index.clamp(minIndex, maxIndex);
      const renderedChanged =
        !clampedRenderedIdx.equals(this.renderedIdx) || dirtied;
      const changeset = clampedRenderedIdx.changeset(this.renderedIdx);
      this.renderedIdx = clampedRenderedIdx;

      this._layoutDirtied = false;
      this._contextDirtied = false;

      if (dirtied) {
        changeset.adds = [
          new NumberRange(clampedRenderedIdx.start, clampedRenderedIdx.end),
        ];
      }

      return {
        visibleChanged,
        renderedChanged,
        changeset,
      };
    }

    /**
     * Say that this reconcile is working against a window shorter than the
     * strip it was asked about — at the level the REASON earns.
     *
     * For `stall` and `span-ceiling` the CAUSE is already reported by the
     * window itself at error (`scroll-window/extend-stalled`,
     * `scroll-window/extend-span-ceiling` with its `witness/` line), so this
     * does not witness again — it adds the consequence, which only this layer
     * knows: these are the pixels a reconcile wanted and this is the span its
     * ranges will be read against.
     *
     * `reach` has no such cause, because it is not a fault: the walk stopped at
     * the end of the addressable band, which is a scroll limit and the designed
     * termination of a legal walk. {@link ScrollWindow.reportReachClamped}
     * deliberately reports it at info with no `witness/`, so an error here was
     * the whole chain's only error and it contradicted the layer that knows why
     * the walk stopped. The consequence is still worth a line — the reached span
     * IS what the ranges below resolve against — but it is a warning:
     *
     *     {"reason":"reach","dir":"min","from":-8388918,"to":-8387343,
     *      "reachedFrom":-5497350,"reachedTo":0}
     *
     * is a fresh account landing on `/sheet/:calendar` with the strip still at
     * the virtual origin ({@link Scrollable.Dom} centres a 16777200px strip, so
     * an un-scrolled read is ~-8.4M px), and the same line with
     * `reachedFrom:-2669340` is `sheets/strip-edge-stays-on-axis`, which jams
     * `scrollLeft` to 0 on purpose and REQUIRES the matching
     * `scroll/window-reach-clamped`. Both were runs failed by a line reporting
     * that the clamp did its job.
     */
    private reportShortExtend(outcome: ScrollWindow.ExtendOutcome.Short): void {
      const payload = {
        reason: outcome.reason,
        dir: outcome.dir,
        from: outcome.requested.start,
        to: outcome.requested.end,
        reachedFrom: outcome.reached.start,
        reachedTo: outcome.reached.end,
      };
      const report =
        outcome.reason === "reach" ? Tracing.warn("ui") : Tracing.err("ui");
      report("core/scroll-engine/reconcile-short-window", payload);
      Tracing.log("scroll/reconcile-short-window", payload);
    }

    contains(index: number): boolean {
      return index >= this.renderedIdx.start && index < this.renderedIdx.end;
    }
  }
}
